/* Which items of a grouping are drawn, given the groups the reader has closed.
 *
 * Pure and index-free, over the flattened item list the grouping engine produced. Everything the
 * grouped builder has to decide follows from one walk: which items are marks, which references
 * survive and where their endpoints land, and what is behind each closed group.
 *
 * The setting is the state, as it is in every other view that collapses - see `isGroupCollapsed`
 * in `buildBlocksGraph`. The graph is rebuilt whenever `collapsedGroupIds` changes, so there is
 * nothing to apply after the fact and no question of whether it has been applied yet.
 */

import { WILDCARD } from '@ardoq/common-helpers';
import type { RawGraphNode2 } from '@ardoq/graph';

/** One entry of `toFlattenedGroupItems`: an item and the id of the item that contains it. */
export type FlattenedGroupItem = {
  item: RawGraphNode2;
  parentId: string | null;
};

export type GroupCollapse = {
  /**
   * The closed group each hidden item is folded behind, by item id.
   *
   * The *outermost* closed group rather than the nearest one, because that is the mark actually
   * on screen: a closed group inside another closed group is itself not drawn, so an item
   * resolving to it would resolve to something the reader cannot see. Absent for every item that
   * is drawn, which is what makes `has` the visibility test.
   */
  foldedInto: ReadonlyMap<string, string>;
  /** The groups drawn closed, and drawn - one nested inside another closed group is neither. */
  collapsedIds: ReadonlySet<string>;
  /** Everything behind each closed group, in the order the walk met it. */
  membersOf: ReadonlyMap<string, RawGraphNode2[]>;
  /**
   * Every group that could be closed, open or not, in item order.
   *
   * Needed to expand one group out of a stored wildcard, which is the one operation the setting
   * cannot express incrementally: "every group except this one" has to be written as the list.
   * Same move the relationships view makes in `toggleCollapsed`.
   */
  collapsibleIds: readonly string[];
  /** Whether the grouping has anything to collapse at all, which is what the buttons gate on. */
  hasGroups: boolean;
};

/**
 * Where a reference's endpoint lands once its group is closed.
 *
 * The endpoint itself where it is drawn, and the group it is folded behind where it is not. A
 * reference between two components in the same closed group resolves to the same id at both ends
 * and is dropped by the caller's self-loop check, which is the right answer: inside a closed
 * group there is nothing for it to join.
 */
export const toDrawnEndpoint = (
  { foldedInto }: GroupCollapse,
  itemId: string
): string => foldedInto.get(itemId) ?? itemId;

/**
 * The groups closed and what is behind them, from the persisted setting.
 *
 * Takes the item list in the order `toFlattenedGroupItems` produced it, which is a preorder: a
 * parent is always decided before its children, so one pass is enough and no item is ever
 * revisited. The wildcard is the convention every other view stores for "every group", and it is
 * read here rather than expanded into a list so a grouping that gains a group while the setting
 * is stored still opens fully closed.
 */
export const toGroupCollapse = (
  items: readonly FlattenedGroupItem[],
  collapsedGroupIds: readonly string[]
): GroupCollapse => {
  const hasChildren = new Set(
    items
      .map(({ parentId }) => parentId)
      .filter((parentId): parentId is string => parentId !== null)
  );
  /* Off the item order rather than off the set, so the list a wildcard expands into is the same
     list on every rebuild - a `Set` preserves insertion order, which here is parent-discovery
     order and not the order a reader sees. */
  const collapsibleIds = items
    .map(({ item }) => item.id)
    .filter(id => hasChildren.has(id));
  const isCollapseAll = collapsedGroupIds.includes(WILDCARD);
  const closed = new Set(collapsedGroupIds);
  const isClosed = (id: string): boolean =>
    hasChildren.has(id) && (isCollapseAll || closed.has(id));

  const foldedInto = new Map<string, string>();
  const collapsedIds = new Set<string>();
  const membersOf = new Map<string, RawGraphNode2[]>();

  for (const { item, parentId } of items) {
    if (parentId === null) {
      if (isClosed(item.id)) collapsedIds.add(item.id);
      continue;
    }

    const behind =
      foldedInto.get(parentId) ?? (isClosed(parentId) ? parentId : null);
    if (behind === null) {
      if (isClosed(item.id)) collapsedIds.add(item.id);
      continue;
    }

    foldedInto.set(item.id, behind);
    const members = membersOf.get(behind);
    if (members) {
      members.push(item);
    } else {
      membersOf.set(behind, [item]);
    }
  }

  return {
    foldedInto,
    collapsedIds,
    membersOf,
    collapsibleIds,
    hasGroups: collapsibleIds.length > 0,
  };
};
